import type { HttpTransport } from '@mangovoice/api-client'
import type { PaginatedApiResult } from '@mangovoice/types'
import type { components } from '@mangovoice/types/generated/mango-api'

import { stringifyParams } from './utils'

type ScheduleSchema = components['schemas']['Schedule']
type _ScheduleCreate = components['schemas']['ScheduleCreate']

export type Schedule = ScheduleSchema

export function createSchedulesResource(transport: HttpTransport) {
  return {
    getSchedules: (params: { pbx_id: number; limit?: number }) => {
      const qs = stringifyParams({
        pbx_id: params.pbx_id,
        limit: params.limit ?? 300,
      })
      return transport.get<PaginatedApiResult<Schedule>>(
        `/app/v1/schedules/?${qs}`,
      )
    },

    getSchedule: (scheduleId: number) =>
      transport.get<Schedule>(`/app/v1/schedules/${scheduleId}/`),

    createSchedule: (data: Partial<Schedule>) =>
      transport.post<Schedule>('/app/v1/schedules/', data),

    updateSchedule: (scheduleId: number, data: Partial<Schedule>) =>
      transport.put<Schedule>(`/app/v1/schedules/${scheduleId}/`, data),

    patchSchedule: (scheduleId: number, data: Partial<Schedule>) =>
      transport.patch<Schedule>(`/app/v1/schedules/${scheduleId}/`, data),

    deleteSchedule: (scheduleId: number) =>
      transport.delete<void>(`/app/v1/schedules/${scheduleId}/`),

    invalidateCache: (scheduleId: number) =>
      transport.put<void>(`/app/v1/schedules/${scheduleId}/invalidate-cache/`),

    // Schedule Events
    listEvents: (scheduleId: number) =>
      transport.get<unknown>(`/app/v1/schedules/${scheduleId}/events/`),

    getEvent: (scheduleId: number, eventId: number) =>
      transport.get<unknown>(
        `/app/v1/schedules/${scheduleId}/events/${eventId}/`,
      ),

    createEvent: (scheduleId: number, data: Record<string, unknown>) =>
      transport.post<unknown>(`/app/v1/schedules/${scheduleId}/events/`, data),

    updateEvent: (
      scheduleId: number,
      eventId: number,
      data: Record<string, unknown>,
    ) =>
      transport.put<unknown>(
        `/app/v1/schedules/${scheduleId}/events/${eventId}/`,
        data,
      ),

    patchEvent: (
      scheduleId: number,
      eventId: number,
      data: Record<string, unknown>,
    ) =>
      transport.patch<unknown>(
        `/app/v1/schedules/${scheduleId}/events/${eventId}/`,
        data,
      ),

    deleteEvent: (scheduleId: number, eventId: number) =>
      transport.delete<void>(
        `/app/v1/schedules/${scheduleId}/events/${eventId}/`,
      ),

    /** Get available holidays (uses legacy path without /app/v1 prefix) */
    getHolidays: () => transport.get<unknown>('/schedule_holidays/'),
  }
}
